import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, TableWrap, Th, Td, UserLink, MarketTag } from '../components/ui/bits';
import { MrrTiles } from '../components/MrrTiles';
import { useRpc } from '../lib/queries';
import { useStripeMrr } from '../hooks/useStripeMrr';
import { classMeta } from '../lib/classes';
import { planLabel, sentence } from '../lib/labels';
import { fmtDate, agoLabel, fmtDateTime } from '../lib/format';
import { fmtMinor, LIST_PRICE } from '../lib/money';
import { sumMetric, type DailyMetric, type RevenueResp } from '../lib/types';

/** Money in the currency it was charged in. Current MRR from Stripe; counts from the database; revenue events from 29 Oct. */
export const RevenuePage: React.FC = () => {
  const { data: r, error, isLoading } = useRpc<RevenueResp>('admin_dash_revenue');
  const stripe = useStripeMrr();
  // Days since tracking began on 29 Oct (London), so the totals cover exactly that span.
  const since = Math.max(1, Math.ceil((Date.now() - Date.parse('2026-10-29T00:00:00+01:00')) / 86_400_000));
  const dm = useRpc<DailyMetric[]>('admin_daily_metrics', { p_days: since });

  if (isLoading) return <LoadingRow label="Loading revenue" />;
  if (error || !r) return <ErrorPanel error={error ?? 'No data returned'} label="admin_dash_revenue" />;

  const count = (pred: (c: RevenueResp['classes'][number]) => boolean) => r.classes.filter(pred).reduce((a, c) => a + c.n, 0);
  const paying = (m: string, plan?: string[]) => count(c => c.stripe_linked && ['paying', 'cancelling'].includes(c.class) && c.market === m && (!plan || plan.includes(c.plan ?? '')));
  const rows = (dm.data ?? []).filter(d => d.day >= '2026-10-29');
  const m = (k: keyof DailyMetric, mk?: 'uk' | 'us') => sumMetric(rows, k, mk);
  const packs = (m: string) => r.packs.filter(p => p.market === m);
  const packListValue = (m: 'uk' | 'us') => packs(m).reduce((a, p) => a + p.paid * (p.tier === 'premium' ? LIST_PRICE[m].premium : p.tier === 'pack' ? LIST_PRICE[m].pack : 0), 0);

  return (
    <div>
      <PageHeader
        title="Revenue"
        subtitle="Pounds and dollars are kept apart and never converted. Current MRR is read from Stripe; subscriber counts from the database; revenue events exist from 29 Oct."
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
        <MrrTiles />
        <StatTile label="Monthly subscribers" value={paying('uk', ['base', 'pro']) + paying('us', ['base', 'pro'])}
          split={{ uk: paying('uk', ['base', 'pro']), us: paying('us', ['base', 'pro']) }} source="Stripe-linked, paying or cancelling. Market from profile, not currency" />
        <StatTile label="Season Pass subscribers" value={paying('uk', ['pass']) + paying('us', ['pass'])}
          split={{ uk: paying('uk', ['pass']), us: paying('us', ['pass']) }} source="Stripe-linked, paying or cancelling" />
        <StatTile label="Past due" value={count(c => c.stripe_linked && c.class === 'past_due')} tone={count(c => c.stripe_linked && c.class === 'past_due') ? 'warn' : 'default'}
          sub={`${count(c => c.stripe_linked && c.class === 'cancelling')} cancelling at period end`} source="admin_sub_classes" />
        <StatTile label="New subscriptions" value={r.subscriptions_started.d30.uk + r.subscriptions_started.d30.us} split={r.subscriptions_started.d30} sub="First paid invoice in the last 30 days" source="processed_stripe_events, first invoice.payment_succeeded per user" />
      </div>

      {stripe.data?.mrr.possiblyTruncated && (
        <div className="mb-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-[12.5px] text-amber-900">
          Stripe returned {stripe.data.mrr.returned} subscriptions, the most one read of admin-api returns, counting every status. Some older active subscriptions may be missing from the MRR above. admin-api needs to page through Stripe for a complete figure.
        </div>
      )}
      {stripe.error && <div className="mb-3"><ErrorPanel error={stripe.error} label="Stripe read (admin-api stripe_overview)" /></div>}

      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        <Section title="Revenue since 29 Oct" sub="From the Stripe webhook events the tracking work records. Older revenue is not in events; Stripe has it." source="admin_daily_metrics (internal accounts excluded)">
          {dm.error ? <ErrorPanel error={dm.error} label="admin_daily_metrics" /> : dm.isLoading ? <LoadingRow /> : (
            <>
              <TableWrap phone="table">
                <thead><tr><Th>Money</Th><Th className="text-right">Pounds</Th><Th className="text-right">Dollars</Th></tr></thead>
                <tbody>
                  <tr><Td className="font-medium text-stone-900">New purchases</Td><Td className="text-right tabular-nums">{fmtMinor(m('new_revenue_gbp'), 'gbp')}</Td><Td className="text-right tabular-nums">{fmtMinor(m('new_revenue_usd'), 'usd')}</Td></tr>
                  <tr><Td className="font-medium text-stone-900">Renewals</Td><Td className="text-right tabular-nums">{fmtMinor(m('renewal_revenue_gbp'), 'gbp')}</Td><Td className="text-right tabular-nums">{fmtMinor(m('renewal_revenue_usd'), 'usd')}</Td></tr>
                  <tr><Td className="font-medium text-stone-900">Refunds</Td><Td className="text-right tabular-nums">{fmtMinor(m('refunds_gbp'), 'gbp')}</Td><Td className="text-right tabular-nums">{fmtMinor(m('refunds_usd'), 'usd')}</Td></tr>
                </tbody>
              </TableWrap>
              <p className="text-[12px] text-stone-500 mt-2">
                {m('purchases')} purchases ({m('first_purchases')} first purchases), {m('renewals')} renewals, {m('payment_failures')} failed payments,
                {' '}{m('dunning_recovered')} recovered, {m('cancel_requests')} cancellations requested, {m('subscriptions_ended')} subscriptions ended.
              </p>
            </>
          )}
        </Section>

        <Section title="Checkout since 29 Oct" sub="People per step, by market." source="admin_daily_metrics">
          <TableWrap phone="table">
            <thead><tr><Th>Step</Th><Th className="text-right">UK</Th><Th className="text-right">US</Th></tr></thead>
            <tbody>
              {([
                ['paywall_viewers', 'Saw a paywall'],
                ['checkout_starters', 'Started checkout'],
                ['stripe_sessions', 'Stripe session created'],
                ['purchases', 'Purchased'],
              ] as const).map(([k, label]) => (
                <tr key={k}>
                  <Td className="font-medium text-stone-900">{label}</Td>
                  <Td className="text-right tabular-nums">{m(k, 'uk')}</Td>
                  <Td className="text-right tabular-nums">{m(k, 'us')}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          {r.paywall_triggers.length > 0 && (
            <p className="text-[12px] text-stone-500 mt-2">Paywalls by trigger: {r.paywall_triggers.map(t => `${sentence(t.trigger)} ${t.n}`).join(', ')}</p>
          )}
        </Section>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        <Section title="Packs" sub="Paid means a Stripe payment intent is recorded and it was not refunded. Value is at list price in the market’s currency, not what Stripe charged." source="firm_passes">
          <TableWrap>
            <thead><tr><Th>Market</Th><Th>Tier</Th><Th className="text-right">Paid</Th><Th className="text-right">Paid, 30 days</Th><Th className="text-right">Refunded</Th><Th className="text-right">Comped</Th></tr></thead>
            <tbody>
              {r.packs.map(p => (
                <tr key={`${p.market}-${p.tier}`}>
                  <Td><MarketTag market={p.market} /></Td>
                  <Td>{planLabel(p.tier)}</Td>
                  <Td className="text-right tabular-nums">{p.paid}</Td>
                  <Td className="text-right tabular-nums">{p.paid_30d}</Td>
                  <Td className="text-right tabular-nums">{p.refunded}</Td>
                  <Td className="text-right tabular-nums">{p.comped}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <p className="text-[12px] text-stone-500 mt-2">
            At list price: UK {fmtMinor(packListValue('uk') * 100, 'gbp')}, US {fmtMinor(packListValue('us') * 100, 'usd')}, all time.
          </p>
        </Section>

        <Section title="Needs attention" sub="Past due, cancelling, lapsed and expired comps." source="admin_sub_classes">
          {r.attention.length === 0 ? <EmptyNote>Nothing here</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>User</Th><Th>State</Th><Th>Plan</Th><Th>Market</Th><Th>Period end</Th></tr></thead>
              <tbody>
                {r.attention.map(a => (
                  <tr key={a.user_id}>
                    <Td><UserLink id={a.user_id} label={a.email} /></Td>
                    <Td><Badge tone={classMeta(a.class).tone}>{classMeta(a.class).label}</Badge></Td>
                    <Td>{planLabel(a.plan)}</Td>
                    <Td><MarketTag market={a.market} /></Td>
                    <Td className="whitespace-nowrap">{fmtDate(a.current_period_end)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        <Section title="Stripe subscriptions" sub="As Stripe returned them, newest first. Prices before coupons and tax." source="admin-api stripe_overview">
          {stripe.isLoading ? <LoadingRow label="Reading Stripe" /> : !stripe.data ? <EmptyNote>No Stripe data</EmptyNote> : (
            <div className="max-h-[420px] overflow-y-auto">
              <TableWrap>
                <thead><tr><Th>Customer</Th><Th>Status</Th><Th className="text-right">Price</Th><Th>Period end</Th></tr></thead>
                <tbody>
                  {stripe.data.rows.filter(s => s.status !== 'canceled' && s.status !== 'incomplete_expired').map(s => (
                    <tr key={s.id}>
                      <Td className="break-all">{s.email ?? s.customer}</Td>
                      <Td>{sentence(s.status)}{s.cancel_at_period_end ? ', cancelling' : ''}</Td>
                      <Td className="text-right tabular-nums whitespace-nowrap">{s.amount != null && s.currency ? `${fmtMinor(s.amount, s.currency)} a ${s.interval ?? 'period'}` : 'Unknown'}</Td>
                      <Td className="whitespace-nowrap">{fmtDate(s.current_period_end)}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </div>
          )}
        </Section>

        <Section title="Stripe webhooks" sub="Every webhook Stripe sent in the last 24 hours, with failures." source="stripe_webhook_log">
          {!r.webhook_log ? <EmptyNote>The webhook log table is not live yet. It ships with the tracking work.</EmptyNote> : (
            <>
              <p className="text-[12.5px] text-stone-600 mb-2">Last received {r.webhook_log.last_received ? agoLabel(r.webhook_log.last_received) : 'never'}.</p>
              {r.webhook_log.last_24h.length === 0 ? <EmptyNote>None in 24 hours</EmptyNote> : (
                <TableWrap phone="table">
                  <thead><tr><Th>Event</Th><Th>Status</Th><Th className="text-right">Count</Th></tr></thead>
                  <tbody>
                    {r.webhook_log.last_24h.map((w, i) => (
                      <tr key={i}><Td>{w.event_type}</Td><Td><Badge tone={w.status === 'ok' ? 'emerald' : w.status === 'error' ? 'rose' : 'stone'}>{w.status}</Badge></Td><Td className="text-right tabular-nums">{w.n}</Td></tr>
                    ))}
                  </tbody>
                </TableWrap>
              )}
              {r.webhook_log.errors.length > 0 && (
                <div className="mt-3 space-y-1">
                  {r.webhook_log.errors.map(e => (
                    <p key={e.event_id} className="text-[12px] text-rose-700 break-words">{fmtDateTime(e.last_received_at)} · {e.event_type}{e.http_status ? ` (HTTP ${e.http_status}, ${e.attempts ?? 1} attempts)` : ''}: {e.error ?? 'no message'}</p>
                  ))}
                </div>
              )}
            </>
          )}
        </Section>
      </div>

      <Section title="Latest firm passes" sub="Newest 25, every source." source="firm_passes">
        <TableWrap>
          <thead><tr><Th>Firm</Th><Th>Tier</Th><Th>User</Th><Th>Market</Th><Th>Payment</Th><Th>Granted</Th><Th>Expires</Th></tr></thead>
          <tbody>
            {r.latest_passes.map(p => (
              <tr key={p.id}>
                <Td className="font-medium text-stone-900">{p.firm_name ?? 'Unknown firm'}</Td>
                <Td>{planLabel(p.tier)}</Td>
                <Td><UserLink id={p.user_id} label={p.email} /></Td>
                <Td><MarketTag market={p.market} /></Td>
                <Td>
                  {p.refunded ? <Badge tone="rose">Refunded</Badge>
                    : p.paid ? <Badge tone="emerald">Paid</Badge>
                    : p.has_session ? <Badge tone="amber" title="Checkout session recorded but no payment intent">Session, no payment</Badge>
                    : <Badge tone="indigo">Comped</Badge>}
                </Td>
                <Td className="whitespace-nowrap">{fmtDate(p.granted_at)}</Td>
                <Td className="whitespace-nowrap">{fmtDate(p.expires_at)}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Section>
    </div>
  );
};
